import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FaImage,
  FaUpload,
  FaTimes,
  FaPalette,
  FaFont,
  FaShapes,
} from "react-icons/fa";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useStore } from "../../context/StoreContext";
import { api } from "services/api";
import { FontSelector } from "../../components/productCustomization/FontSelector";
import { getLogoUrl } from "../../utils/logoUtils";

interface ThemePalette {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
  text?: string;
  [key: string]: string | undefined;
}

interface ThemeStyles {
  borderRadius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  spacing?: "compact" | "normal" | "loose";
  shadow?: "none" | "sm" | "md" | "lg" | "xl";
  borderWidth?: "thin" | "normal" | "thick";
  contentWidth?: "narrow" | "medium" | "wide" | "full";
}

interface ThemeSettingsProps {
  onSave?: () => void;
  showTopSaveButton?: boolean;
  stepTitle?: string;
}

// Helper function to extract just the font name (removes fallbacks like ", sans-serif")
const extractFontName = (fontValue: string | null | undefined): string => {
  if (!fontValue) return "";
  // Handle formats like "'Roboto', sans-serif" or "Roboto, sans-serif" or just "Roboto"
  const match = fontValue.match(/^['"]?([^'",]+)['"]?/);
  return match ? match[1].trim() : fontValue.split(",")[0].trim();
};

// Helper function to format font name with fallback
// Known serif fonts get "serif" fallback, others get "sans-serif"
const formatFontFamilyForCSS = (fontName: string | null | undefined): string => {
  if (!fontName) return "'Playfair Display', serif";
  
  // Extract just the font name if it contains a comma (legacy format)
  const cleanName = fontName.includes(",") ? extractFontName(fontName) : fontName;
  
  // Known serif fonts
  const serifFonts = [
    "Trirong", "Playfair Display", "Merriweather", "Crimson Text", 
    "Lora", "PT Serif", "Georgia", "Times New Roman", "Garamond", "Palatino"
  ];
  
  const isSerif = serifFonts.some(serif => 
    cleanName.toLowerCase().includes(serif.toLowerCase())
  );
  
  const fallback = isSerif ? "serif" : "sans-serif";
  return cleanName.includes(" ") ? `"${cleanName}", ${fallback}` : `${cleanName}, ${fallback}`;
};

export const ThemeSettings: React.FC<ThemeSettingsProps> = ({
  onSave,
  showTopSaveButton = false,
  stepTitle,
}) => {
  const { t } = useTranslation();
  const { store, refreshStore, loading: storeLoading } = useStore();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [themePalette, setThemePalette] = useState<ThemePalette>({});
  const [themeStyles, setThemeStyles] = useState<ThemeStyles>({});
  const [fontFamily, setFontFamily] = useState<string>("");
  const [logo, setLogo] = useState<string>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState("colors");
  const logoInputRef = useRef<HTMLInputElement>(null);

  const loading = storeLoading;

  // Initialize form fields when store data is loaded
  useEffect(() => {
    if (store && !storeLoading) {
      setThemePalette(store.themePalette || {});
      setThemeStyles(store.themeStyles || {});
      // Extract just the font name (remove any fallbacks like ", sans-serif")
      setFontFamily(extractFontName(store.fontFamily) || "");
      setLogo(store.logo || "");
    } else if (!store && !storeLoading) {
      setThemePalette({});
      setThemeStyles({});
      setFontFamily("");
      setLogo("");
    }
  }, [store, storeLoading]);

  // Real-time preview updates
  useEffect(() => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;

      // Apply theme colors immediately for preview
      root.style.setProperty(
        "--store-primary",
        themePalette.primary || "#3B82F6"
      );
      root.style.setProperty(
        "--store-secondary",
        themePalette.secondary || "#8B5CF6"
      );
      root.style.setProperty(
        "--store-accent",
        themePalette.accent || "#10B981"
      );
      root.style.setProperty(
        "--store-background",
        themePalette.background || "#FFFFFF"
      );
      root.style.setProperty("--store-text", themePalette.text || "#111827");
      root.style.setProperty(
        "--store-font-family",
        formatFontFamilyForCSS(fontFamily)
      );

      // Apply theme styles
      const borderRadiusMap: Record<string, string> = {
        none: "0",
        sm: "0.125rem",
        md: "0.375rem",
        lg: "0.5rem",
        xl: "0.75rem",
        full: "9999px",
      };

      const shadowMap: Record<string, string> = {
        none: "none",
        sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
      };

      const borderWidthMap: Record<string, string> = {
        thin: "1px",
        normal: "2px",
        thick: "4px",
      };

      const spacingMap: Record<string, string> = {
        compact: "0.75",
        normal: "1",
        loose: "1.5",
      };

      const contentWidthMap: Record<string, string> = {
        narrow: "48rem", // 768px - max-w-3xl
        medium: "64rem", // 1024px - max-w-4xl
        wide: "80rem", // 1280px - max-w-7xl
        full: "100%", // Full width
      };

      root.style.setProperty(
        "--store-border-radius",
        borderRadiusMap[themeStyles.borderRadius || "md"]
      );
      root.style.setProperty(
        "--store-shadow",
        shadowMap[themeStyles.shadow || "md"]
      );
      root.style.setProperty(
        "--store-border-width",
        borderWidthMap[themeStyles.borderWidth || "normal"]
      );
      root.style.setProperty(
        "--store-spacing-multiplier",
        spacingMap[themeStyles.spacing || "normal"]
      );
      root.style.setProperty(
        "--store-content-width",
        contentWidthMap[themeStyles.contentWidth || "wide"]
      );
    }
  }, [themePalette, themeStyles, fontFamily]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    setSaving(true);
    try {
      await api.put("/store/theme-settings", {
        themePalette,
        themeStyles,
        fontFamily: fontFamily || null,
        logo: logo || null,
      });
      setSuccess(t("admin.themeSettings.success"));
      setSaving(false);
      if (onSave) {
        onSave();
      }
      await refreshStore();
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("admin.themeSettings.errors.update")
      );
      setSaving(false);
    }
  };

  const updatePaletteColor = (key: string, value: string) => {
    setThemePalette((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const resetPalette = () => {
    setThemePalette({});
  };

  const handleLogoUpload = async (file: File) => {
    setUploadingLogo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/admin/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.url) {
        setLogo(response.data.url);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const handleLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleLogoUpload(file);
  };

  const handleLogoDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      setError("Please drop an image file");
      return;
    }

    handleLogoUpload(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Heading size="xl">
              {stepTitle || t("admin.themeSettings.title")}
            </Heading>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t("admin.themeSettings.description")}
            </p>
          </div>
          {showTopSaveButton && (
            <div className="ml-4">
              <Button
                type="button"
                variant="primary"
                disabled={saving}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e as any);
                }}
              >
                {saving
                  ? t("admin.themeSettings.saving")
                  : t("admin.themeSettings.save")}
              </Button>
            </div>
          )}
        </div>

        {error && <Alert variant="error">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Panel */}
          <div className="space-y-4">
            <Card>
              {/* Tab Navigation */}
              <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-6">
                <button
                  type="button"
                  onClick={() => setActiveTab("colors")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "colors"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <FaPalette className="text-xs" />
                  Colors
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("styles")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "styles"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <FaShapes className="text-xs" />
                  Styles
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("typography")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "typography"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <FaFont className="text-xs" />
                  Typography
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("logo")}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "logo"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <FaImage className="text-xs" />
                  Logo
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <Stack spacing="lg">
                  {/* Colors Tab */}
                  {activeTab === "colors" && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <Heading size="sm">
                          {t("admin.themeSettings.colorPalette")}
                        </Heading>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={resetPalette}
                        >
                          {t("admin.themeSettings.resetColors")}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {t("admin.themeSettings.colorPaletteDescription")}
                      </p>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t("admin.themeSettings.primaryColor")}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={themePalette.primary || "#3B82F6"}
                              onChange={(e) =>
                                updatePaletteColor("primary", e.target.value)
                              }
                              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                              title="Primary color"
                            />
                            <Input
                              type="text"
                              value={themePalette.primary || ""}
                              onChange={(e) =>
                                updatePaletteColor("primary", e.target.value)
                              }
                              placeholder="#3B82F6"
                              className="flex-1"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t("admin.themeSettings.primaryColorDescription")}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t("admin.themeSettings.secondaryColor")}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={themePalette.secondary || "#8B5CF6"}
                              onChange={(e) =>
                                updatePaletteColor("secondary", e.target.value)
                              }
                              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                              title="Secondary color"
                            />
                            <Input
                              type="text"
                              value={themePalette.secondary || ""}
                              onChange={(e) =>
                                updatePaletteColor("secondary", e.target.value)
                              }
                              placeholder="#8B5CF6"
                              className="flex-1"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t("admin.themeSettings.secondaryColorDescription")}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t("admin.themeSettings.accentColor")}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={themePalette.accent || "#10B981"}
                              onChange={(e) =>
                                updatePaletteColor("accent", e.target.value)
                              }
                              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                              title="Accent color"
                            />
                            <Input
                              type="text"
                              value={themePalette.accent || ""}
                              onChange={(e) =>
                                updatePaletteColor("accent", e.target.value)
                              }
                              placeholder="#10B981"
                              className="flex-1"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t("admin.themeSettings.accentColorDescription")}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t("admin.themeSettings.backgroundColor")}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={themePalette.background || "#FFFFFF"}
                              onChange={(e) =>
                                updatePaletteColor("background", e.target.value)
                              }
                              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                              title="Background color"
                            />
                            <Input
                              type="text"
                              value={themePalette.background || ""}
                              onChange={(e) =>
                                updatePaletteColor("background", e.target.value)
                              }
                              placeholder="#FFFFFF"
                              className="flex-1"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t(
                              "admin.themeSettings.backgroundColorDescription"
                            )}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t("admin.themeSettings.textColor")}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="color"
                              value={themePalette.text || "#111827"}
                              onChange={(e) =>
                                updatePaletteColor("text", e.target.value)
                              }
                              className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                              title="Text color"
                            />
                            <Input
                              type="text"
                              value={themePalette.text || ""}
                              onChange={(e) =>
                                updatePaletteColor("text", e.target.value)
                              }
                              placeholder="#111827"
                              className="flex-1"
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {t("admin.themeSettings.textColorDescription")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Styles Tab */}
                  {activeTab === "styles" && (
                    <div>
                      <Heading size="sm" className="mb-4">
                        Theme Styles
                      </Heading>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        Customize the visual appearance of your store with
                        border radius, spacing, shadows, and border widths.
                      </p>
                      <div className="space-y-4">
                        <div>
                          <Select
                            label="Border Radius"
                            options={[
                              { value: "none", label: "None (Sharp)" },
                              { value: "sm", label: "Small" },
                              { value: "md", label: "Medium" },
                              { value: "lg", label: "Large" },
                              { value: "xl", label: "Extra Large" },
                              { value: "full", label: "Full (Rounded)" },
                            ]}
                            value={themeStyles.borderRadius || "md"}
                            onChange={(e) =>
                              setThemeStyles((prev) => ({
                                ...prev,
                                borderRadius: e.target
                                  .value as ThemeStyles["borderRadius"],
                              }))
                            }
                            helperText="Controls the roundness of corners on buttons, cards, and other elements"
                          />
                        </div>

                        <div>
                          <Select
                            label="Spacing"
                            options={[
                              { value: "compact", label: "Compact" },
                              { value: "normal", label: "Normal" },
                              { value: "loose", label: "Loose" },
                            ]}
                            value={themeStyles.spacing || "normal"}
                            onChange={(e) =>
                              setThemeStyles((prev) => ({
                                ...prev,
                                spacing: e.target
                                  .value as ThemeStyles["spacing"],
                              }))
                            }
                            helperText="Controls the spacing between elements (padding, margins)"
                          />
                        </div>

                        <div>
                          <Select
                            label="Shadow"
                            options={[
                              { value: "none", label: "None" },
                              { value: "sm", label: "Small" },
                              { value: "md", label: "Medium" },
                              { value: "lg", label: "Large" },
                              { value: "xl", label: "Extra Large" },
                            ]}
                            value={themeStyles.shadow || "md"}
                            onChange={(e) =>
                              setThemeStyles((prev) => ({
                                ...prev,
                                shadow: e.target.value as ThemeStyles["shadow"],
                              }))
                            }
                            helperText="Controls the shadow depth on cards and elevated elements"
                          />
                        </div>

                        <div>
                          <Select
                            label="Border Width"
                            options={[
                              { value: "thin", label: "Thin" },
                              { value: "normal", label: "Normal" },
                              { value: "thick", label: "Thick" },
                            ]}
                            value={themeStyles.borderWidth || "normal"}
                            onChange={(e) =>
                              setThemeStyles((prev) => ({
                                ...prev,
                                borderWidth: e.target
                                  .value as ThemeStyles["borderWidth"],
                              }))
                            }
                            helperText="Controls the thickness of borders on elements"
                          />
                        </div>

                        <div>
                          <Select
                            label="Content Width"
                            options={[
                              { value: "narrow", label: "Narrow (Centered with large margins)" },
                              { value: "medium", label: "Medium (Balanced)" },
                              { value: "wide", label: "Wide (More content, less margins)" },
                              { value: "full", label: "Full Width (Edge to edge)" },
                            ]}
                            value={themeStyles.contentWidth || "wide"}
                            onChange={(e) =>
                              setThemeStyles((prev) => ({
                                ...prev,
                                contentWidth: e.target
                                  .value as ThemeStyles["contentWidth"],
                              }))
                            }
                            helperText="Controls the maximum width of content areas on landing pages. Narrow = centered with large white space, Full = uses entire screen width"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Typography Tab */}
                  {activeTab === "typography" && (
                    <div>
                      <Heading size="sm" className="mb-4">
                        {t("admin.themeSettings.fontFamily")}
                      </Heading>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {t("admin.themeSettings.fontFamilyDescription")}
                      </p>
                      <FontSelector
                        value={fontFamily}
                        onChange={(e) => setFontFamily(e.target.value)}
                        placeholder="Default (System Font)"
                        outputFormat="name"
                      />
                      {fontFamily && (
                        <p
                          className="mt-2 text-sm text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: formatFontFamilyForCSS(fontFamily) }}
                        >
                          {t("admin.themeSettings.fontPreview")}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Logo Tab */}
                  {activeTab === "logo" && (
                    <div>
                      <Heading size="sm" className="mb-4">
                        {t("admin.themeSettings.storeLogo") || "Store Logo"}
                      </Heading>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        {t("admin.themeSettings.storeLogoDescription") ||
                          "Upload your store logo. This will be displayed in your store header and other branding areas."}
                      </p>

                      {/* Logo Preview */}
                      {logo && (
                        <div className="mb-4 relative inline-block">
                          <img
                            src={getLogoUrl(logo)}
                            alt="Store logo"
                            className="h-16 w-auto object-contain border border-gray-300 dark:border-gray-600 p-1 bg-white dark:bg-gray-800 rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => setLogo("")}
                            className="absolute -top-1 -right-1 bg-red-600 text-white p-1 hover:bg-red-700 rounded-full"
                            title={
                              t("admin.themeSettings.removeLogo") ||
                              "Remove logo"
                            }
                          >
                            <FaTimes className="text-xs" />
                          </button>
                        </div>
                      )}

                      {/* Upload Area */}
                      <div
                        className={`border-2 border-dashed p-4 text-center transition-colors rounded-lg ${
                          uploadingLogo
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 opacity-50 pointer-events-none"
                            : dragActive
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                              : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer"
                        }`}
                        onClick={() => logoInputRef.current?.click()}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleLogoDrop}
                      >
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleLogoFileSelect}
                          className="hidden"
                        />
                        <FaImage className="mx-auto text-2xl text-gray-400 dark:text-gray-500 mb-2" />
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                          {uploadingLogo
                            ? t("admin.themeSettings.uploading") ||
                              "Uploading..."
                            : logo
                              ? t("admin.themeSettings.clickToChange") ||
                                "Click to change"
                              : t("admin.themeSettings.clickOrDragToUpload") ||
                                "Click or drag to upload"}
                        </p>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded"
                          disabled={uploadingLogo}
                        >
                          <FaUpload className="text-xs" />
                          {uploadingLogo
                            ? t("admin.themeSettings.uploading") ||
                              "Uploading..."
                            : t("admin.themeSettings.select") || "Select"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving
                        ? t("admin.themeSettings.saving")
                        : t("admin.themeSettings.save")}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setThemePalette(store?.themePalette || {});
                        setThemeStyles(store?.themeStyles || {});
                        setFontFamily(extractFontName(store?.fontFamily) || "");
                        setLogo(store?.logo || "");
                        setError(null);
                        setSuccess(null);
                      }}
                      disabled={saving}
                    >
                      {t("common.reset")}
                    </Button>
                  </div>
                </Stack>
              </form>
            </Card>
          </div>

          {/* Preview Panel - Always Visible */}
          <div className="lg:sticky lg:top-4 h-fit">
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <FaPalette className="text-blue-600" />
                <Heading size="sm">{t("admin.themeSettings.preview")}</Heading>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                {t("admin.themeSettings.previewDescription")}
              </p>

              <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Navigation Bar Preview */}
                <div
                  className="p-3 border border-gray-200 rounded-lg"
                  style={{
                    backgroundColor: themePalette.background || "#FFFFFF",
                    fontFamily: formatFontFamilyForCSS(fontFamily),
                  }}
                >
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {t("admin.themeSettings.previewNavigationBar")}
                  </h4>
                  <div
                    className="flex items-center justify-between p-3 rounded"
                    style={{
                      backgroundColor: themePalette.primary || "#3B82F6",
                      color: "#FFFFFF",
                      borderRadius: "var(--store-border-radius, 0.375rem)",
                      boxShadow:
                        "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                      fontFamily: formatFontFamilyForCSS(fontFamily),
                    }}
                  >
                    <div className="font-bold text-sm">
                      {t("admin.themeSettings.previewYourStore")}
                    </div>
                    <div className="flex gap-3 text-xs">
                      <span className="cursor-pointer hover:opacity-80">
                        {t("common.home")}
                      </span>
                      <span className="cursor-pointer hover:opacity-80">
                        {t("common.products")}
                      </span>
                      <span className="cursor-pointer hover:opacity-80">
                        {t("admin.themeSettings.previewAbout")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Product Card Preview */}
                <div
                  className="p-3 border border-gray-200 rounded-lg"
                  style={{
                    backgroundColor: themePalette.background || "#FFFFFF",
                    fontFamily: formatFontFamilyForCSS(fontFamily),
                  }}
                >
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {t("admin.themeSettings.previewProductCard")}
                  </h4>
                  <div
                    className="border overflow-hidden"
                    style={{
                      borderColor: themePalette.accent || "#E5E7EB",
                      backgroundColor: themePalette.background || "#FFFFFF",
                      borderRadius: "var(--store-border-radius, 0.375rem)",
                      boxShadow:
                        "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                      borderWidth: "var(--store-border-width, 2px)",
                    }}
                  >
                    <div
                      className="h-20 flex items-center justify-center"
                      style={{
                        backgroundColor: themePalette.accent || "#E5E7EB",
                      }}
                    >
                      <span
                        className="text-xs"
                        style={{ color: themePalette.text || "#111827" }}
                      >
                        {t("admin.themeSettings.previewProductImage")}
                      </span>
                    </div>
                    <div className="p-3">
                      <h5
                        className="text-sm font-semibold mb-1"
                        style={{ color: themePalette.text || "#111827" }}
                      >
                        {t("admin.themeSettings.previewProductName")}
                      </h5>
                      <p
                        className="text-xs mb-2"
                        style={{ color: themePalette.text || "#111827" }}
                      >
                        {t("admin.themeSettings.previewProductDescription")}
                      </p>
                      <div className="flex items-center justify-between">
                        <span
                          className="text-sm font-bold"
                          style={{ color: themePalette.primary || "#3B82F6" }}
                        >
                          $99.99
                        </span>
                        <button
                          type="button"
                          className="px-3 py-1 font-semibold text-xs text-white transition-colors"
                          style={{
                            backgroundColor: themePalette.primary || "#3B82F6",
                            color: "#FFFFFF",
                            borderRadius:
                              "var(--store-border-radius, 0.375rem)",
                            boxShadow:
                              "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                            fontFamily:
                              fontFamily || "'Playfair Display', serif",
                          }}
                        >
                          {t("admin.themeSettings.previewAddToCart")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Buttons Preview */}
                <div
                  className="p-3 border border-gray-200 rounded-lg"
                  style={{
                    backgroundColor: themePalette.background || "#FFFFFF",
                    fontFamily: formatFontFamilyForCSS(fontFamily),
                  }}
                >
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {t("admin.themeSettings.previewButtons")}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 font-semibold text-xs text-white transition-colors"
                      style={{
                        backgroundColor: themePalette.primary || "#3B82F6",
                        color: "#FFFFFF",
                        borderRadius: "var(--store-border-radius, 0.375rem)",
                        boxShadow:
                          "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                        fontFamily: formatFontFamilyForCSS(fontFamily),
                      }}
                    >
                      {t("admin.themeSettings.previewPrimaryButton")}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 font-semibold text-xs transition-colors"
                      style={{
                        borderColor: themePalette.secondary || "#8B5CF6",
                        color: themePalette.secondary || "#8B5CF6",
                        backgroundColor: "transparent",
                        borderWidth: "var(--store-border-width, 2px)",
                        borderRadius: "var(--store-border-radius, 0.375rem)",
                        fontFamily: formatFontFamilyForCSS(fontFamily),
                      }}
                    >
                      {t("admin.themeSettings.previewSecondaryButton")}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 font-semibold text-xs text-white transition-colors"
                      style={{
                        backgroundColor: themePalette.accent || "#10B981",
                        color: "#FFFFFF",
                        borderRadius: "var(--store-border-radius, 0.375rem)",
                        boxShadow:
                          "var(--store-shadow, 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1))",
                        fontFamily: formatFontFamilyForCSS(fontFamily),
                      }}
                    >
                      {t("admin.themeSettings.previewAccentButton")}
                    </button>
                  </div>
                </div>

                {/* Typography Preview */}
                <div
                  className="p-3 border border-gray-200 rounded-lg"
                  style={{
                    backgroundColor: themePalette.background || "#FFFFFF",
                    color: themePalette.text || "#111827",
                    fontFamily: fontFamily || "'Playfair Display', serif",
                  }}
                >
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {t("admin.themeSettings.previewTypography")}
                  </h4>
                  <h1
                    className="text-2xl font-bold mb-2"
                    style={{ color: themePalette.primary || "#3B82F6" }}
                  >
                    {t("admin.themeSettings.previewHeading1")}
                  </h1>
                  <h2
                    className="text-lg font-semibold mb-2"
                    style={{ color: themePalette.text || "#111827" }}
                  >
                    {t("admin.themeSettings.previewHeading2")}
                  </h2>
                  <h3
                    className="text-base font-semibold mb-2"
                    style={{ color: themePalette.text || "#111827" }}
                  >
                    {t("admin.themeSettings.previewHeading3")}
                  </h3>
                  <p className="text-sm mb-2">
                    {t("admin.themeSettings.previewParagraph")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("admin.themeSettings.previewSmallText")}
                  </p>
                </div>

                {/* Form Elements Preview */}
                <div
                  className="p-3 border border-gray-200 rounded-lg"
                  style={{
                    backgroundColor: themePalette.background || "#FFFFFF",
                    fontFamily: formatFontFamilyForCSS(fontFamily),
                  }}
                >
                  <h4 className="text-xs font-semibold text-gray-600 mb-2">
                    {t("admin.themeSettings.previewFormElements")}
                  </h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder={t(
                        "admin.themeSettings.previewInputPlaceholder"
                      )}
                      className="w-full px-3 py-2 text-sm border focus:outline-none"
                      style={{
                        borderColor: themePalette.accent || "#E5E7EB",
                        color: themePalette.text || "#111827",
                        backgroundColor: themePalette.background || "#FFFFFF",
                        borderWidth: "var(--store-border-width, 2px)",
                        borderRadius: "var(--store-border-radius, 0.375rem)",
                        fontFamily: formatFontFamilyForCSS(fontFamily),
                      }}
                    />
                    <select
                      className="w-full px-3 py-2 text-sm border focus:outline-none"
                      style={{
                        borderColor: themePalette.accent || "#E5E7EB",
                        color: themePalette.text || "#111827",
                        backgroundColor: themePalette.background || "#FFFFFF",
                        borderWidth: "var(--store-border-width, 2px)",
                        borderRadius: "var(--store-border-radius, 0.375rem)",
                        fontFamily: formatFontFamilyForCSS(fontFamily),
                      }}
                    >
                      <option>{t("common.select")}</option>
                      <option>{t("admin.themeSettings.previewOption1")}</option>
                      <option>{t("admin.themeSettings.previewOption2")}</option>
                    </select>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </Stack>
    </Container>
  );
};
